import React, { useEffect, useRef, useState, useCallback } from "react";
import { Row, Col, Card, Button, Spinner, Alert, Form, Modal } from "react-bootstrap";
import axios from "axios";
import { Link } from "react-router-dom";
import PageLayout from "../../layouts/PageLayout";

export default function DispatchProductsPage() {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [products, setProducts] = useState([]);
    const [courierCompanies, setCourierCompanies] = useState([]);

    // Form States
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [courierCompanyId, setCourierCompanyId] = useState("");
    const [trackingNumber, setTrackingNumber] = useState("");

    // Dispatched Image & Camera States
    const [dispatchedImage, setDispatchedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);

    const videoRef = useRef(null);
    const streamRef = useRef(null);

    // Stop and cleanup camera media streams
    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraOpen(false);
    }, []);

    // Camera cleanup on unmount
    useEffect(() => {
        return () => {
            stopCamera();
        };
    }, [stopCamera]);

    // Cleanup Blob URLs to prevent memory leaks
    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    // Fetch approved products & courier companies
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem("authToken");
            const headers = { Authorization: `Bearer ${token}` };

            const [productsRes, initialDataRes] = await Promise.allSettled([
                axios.get(`${process.env.REACT_APP_API_URL}/store-seller/approved-products`, { headers }),
                axios.get(`${process.env.REACT_APP_API_URL}/store-seller/initial-data`, { headers }),
            ]);

            if (productsRes.status === "fulfilled" && productsRes.value.data?.data) {
                setProducts(productsRes.value.data.data);
            }

            if (initialDataRes.status === "fulfilled") {
                const data = initialDataRes.value?.data || {};

                setCourierCompanies(data.courier_companies || []);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
            setError("Failed to load required dispatch data.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Product Selection Handlers
    const toggleProduct = (productId) => {
        setSelectedProducts((prev) =>
            prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
        );
    };

    const selectAllProducts = () => {
        setSelectedProducts(products.map((p) => p.id));
    };

    const clearSelectedProducts = () => {
        setSelectedProducts([]);
    };

    // Image Upload Handler
    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setError("Please select a valid image file.");
            return;
        }

        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setDispatchedImage(file);
        setImagePreview(URL.createObjectURL(file));
    };

    // Open Camera
    const openCamera = async () => {
        try {
            setError(null);
            if (!navigator.mediaDevices?.getUserMedia) {
                setError("Camera is not supported by this browser.");
                return;
            }

            stopCamera();

            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: "environment" },
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                },
                audio: false,
            });

            streamRef.current = stream;
            setCameraOpen(true);

            setTimeout(() => {
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play().catch((err) => console.error("Video play error:", err));
                }
            }, 100);
        } catch (err) {
            console.error("Camera error:", err);
            if (err.name === "NotAllowedError") {
                setError("Camera permission denied. Please enable camera access.");
            } else if (err.name === "NotFoundError") {
                setError("No camera device found.");
            } else {
                setError("Unable to open camera.");
            }
        }
    };

    // Capture Photo from Camera
    const capturePhoto = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth || !video.videoHeight) {
            setError("Camera is not ready. Please wait.");
            return;
        }

        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const context = canvas.getContext("2d");
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    setError("Unable to capture photo.");
                    return;
                }

                const file = new File([blob], `dispatched-shipment-${Date.now()}.jpg`, {
                    type: "image/jpeg",
                });

                if (imagePreview) URL.revokeObjectURL(imagePreview);
                setDispatchedImage(file);
                setImagePreview(URL.createObjectURL(file));
                stopCamera();
            },
            "image/jpeg",
            0.9
        );
    };

    const removeDispatchedImage = () => {
        if (imagePreview) URL.revokeObjectURL(imagePreview);
        setDispatchedImage(null);
        setImagePreview(null);
    };

    const getProductImage = (product) => {
        if (product.card_img) {
            return `${process.env.REACT_APP_IMG_URL}${product.card_img}`;
        }
        if (product.images?.[0]?.image) {
            return `${process.env.REACT_APP_IMG_URL}${product.images[0].image}`;
        }
        return null;
    };

    // Submit Dispatch API
    const handleDispatchProducts = async () => {
        setError(null);
        setSuccess(null);

        if (!courierCompanyId) return setError("Please select a courier company.");
        if (!trackingNumber.trim()) return setError("Please enter a tracking number.");
        if (selectedProducts.length === 0) return setError("Please select at least one product to dispatch.");
        if (!dispatchedImage) return setError("Please capture or upload a dispatch proof image.");

        setSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("courier_company_id", courierCompanyId);
            formData.append("tracking_number", trackingNumber);
            formData.append("dispatched_image", dispatchedImage);

            selectedProducts.forEach((productId, index) => {
                formData.append(`product_ids[${index}][product_id]`, productId);
            });

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/store-seller/dispatched-product`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            if (response.data?.success) {
                setSuccess(response.data?.message || "Product shipment created successfully.");
                setSelectedProducts([]);
                setCourierCompanyId("");
                setTrackingNumber("");
                removeDispatchedImage();
                await fetchData();
            } else {
                setError(response.data?.message || "Unable to create product shipment.");
            }
        } catch (err) {
            console.error("Dispatch error:", err);
            if (err.response?.status === 422) {
                const validationErrors = err.response.data.errors;
                setError(
                    validationErrors
                        ? Object.values(validationErrors).flat().join(" ")
                        : err.response.data.message || "Validation failed."
                );
            } else {
                setError(err.response?.data?.message || "Failed to submit product dispatch.");
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading dispatch products...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                {/* Breadcrumb */}
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Dispatch Store Products</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">Home</Link>
                                </li>
                                <li className="mc-breadcrumb-item">Products</li>
                                <li className="mc-breadcrumb-item">Dispatch Products</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Alerts */}
                <Col xl={12}>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
                            {success}
                        </Alert>
                    )}
                </Col>

                {/* Product Selection List */}
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h4 className="mb-1">Approved Products</h4>
                                <small className="text-muted">
                                    {products.length} products available for dispatch
                                </small>
                            </div>

                            <div>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={selectAllProducts}
                                >
                                    Select All
                                </Button>
                                <Button variant="secondary" size="sm" onClick={clearSelectedProducts}>
                                    Clear
                                </Button>
                            </div>
                        </div>

                        {products.length === 0 ? (
                            <Alert variant="info">No approved products available for dispatch.</Alert>
                        ) : (
                            <Row>
                                {products.map((product) => {
                                    const selected = selectedProducts.includes(product.id);
                                    const imageUrl = getProductImage(product);

                                    return (
                                        <Col key={product.id} xs={12} sm={6} md={4} lg={3} className="mb-4">
                                            <Card
                                                className={selected ? "h-100 border-primary shadow-sm" : "h-100"}
                                                style={{ cursor: "pointer", borderWidth: selected ? "2px" : "1px" }}
                                                onClick={() => toggleProduct(product.id)}
                                            >
                                                <div
                                                    style={{
                                                        height: "180px",
                                                        background: "#f8f9fa",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {imageUrl ? (
                                                        <img
                                                            src={imageUrl}
                                                            alt={product.title}
                                                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "https://via.placeholder.com/180?text=No+Img";
                                                            }}
                                                        />
                                                    ) : (
                                                        <span className="text-muted">No Image</span>
                                                    )}
                                                </div>

                                                <Card.Body>
                                                    <div className="d-flex align-items-start">
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selected}
                                                            onChange={() => toggleProduct(product.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="me-2 mt-1"
                                                        />
                                                        <div>
                                                            <h6 className="mb-1 text-truncate" style={{ maxWidth: "160px" }}>
                                                                {product.title}
                                                            </h6>
                                                            <small className="text-muted d-block">
                                                                {product.product_code}
                                                            </small>
                                                        </div>
                                                    </div>

                                                    <div className="mt-3">
                                                        <small className="d-block text-truncate">
                                                            Category: <strong>{product.category?.name || "N/A"}</strong>
                                                        </small>
                                                        <small className="d-block text-truncate">
                                                            Brand: <strong>{product.brand?.name || "N/A"}</strong>
                                                        </small>
                                                        <small className="d-block text-capitalize">
                                                            Condition: <strong>{product.product_condition}</strong>
                                                        </small>

                                                        <div className="d-flex justify-content-between align-items-center mt-2">
                                                            <strong className="text-success">
                                                                ${product.sell_price_dollar ?? product.buying_price}
                                                            </strong>
                                                            <small className="text-muted">Qty: {product.product_qty}</small>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}
                    </div>
                </Col>

                {/* Dispatch Shipment Handover Section */}
                {selectedProducts.length > 0 && (
                    <Col xl={12}>
                        <div className="mc-card mb-4">
                            <h4 className="mb-1">Shipment Handover & Details</h4>
                            <p className="text-muted mb-4">
                                Enter shipping details and upload a photo proof of dispatch.
                            </p>

                            <Row>
                                {/* Image Section */}
                                <Col md={6} className="mb-3">
                                    <div
                                        style={{
                                            border: "2px dashed #ddd",
                                            borderRadius: "10px",
                                            padding: "20px",
                                            textAlign: "center",
                                        }}
                                    >
                                        {imagePreview ? (
                                            <>
                                                <img
                                                    src={imagePreview}
                                                    alt="Dispatched Proof"
                                                    style={{
                                                        width: "100%",
                                                        maxHeight: "350px",
                                                        objectFit: "contain",
                                                        borderRadius: "8px",
                                                    }}
                                                />
                                                <div className="mt-3">
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        className="me-2"
                                                        onClick={removeDispatchedImage}
                                                    >
                                                        Remove Photo
                                                    </Button>
                                                    <Button variant="dark" size="sm" onClick={openCamera}>
                                                        Retake Photo
                                                    </Button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: "50px", marginBottom: "15px" }}>📦</div>
                                                <h5>Dispatch Proof Photo</h5>
                                                <p className="text-muted">
                                                    Take or upload a photo of the shipment parcel
                                                </p>
                                                <Button variant="primary" className="me-2" onClick={openCamera}>
                                                    📷 Take Photo
                                                </Button>
                                                <label className="btn btn-secondary mb-0">
                                                    Upload Photo
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        hidden
                                                        onChange={handleImageUpload}
                                                    />
                                                </label>
                                            </>
                                        )}
                                    </div>
                                </Col>

                                {/* Form Details & Summary */}
                                <Col md={6}>
                                    <div style={{ background: "#f8f9fa", borderRadius: "10px", padding: "20px" }}>
                                        <h5 className="mb-3">Shipment Form</h5>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">
                                                Courier Company <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                value={courierCompanyId}
                                                onChange={(e) => setCourierCompanyId(e.target.value)}
                                            >
                                                <option value="">Select Courier Company</option>
                                                {courierCompanies.map((courier) => (
                                                    <option key={courier.id} value={courier.id}>
                                                        {courier.company_name || courier.title}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">
                                                Tracking Number <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter tracking number"
                                                value={trackingNumber}
                                                onChange={(e) => setTrackingNumber(e.target.value)}
                                            />
                                        </Form.Group>

                                        <hr />

                                        <div className="mb-3">
                                            <small className="text-muted d-block">Selected Products</small>
                                            <h3>{selectedProducts.length}</h3>
                                        </div>

                                        <div className="mb-4">
                                            <small className="text-muted d-block">Dispatch Proof</small>
                                            <h6>{dispatchedImage ? "✓ Photo attached" : "⚠ Photo required"}</h6>
                                        </div>

                                        <Button
                                            variant="success"
                                            className="w-100"
                                            size="lg"
                                            disabled={
                                                submitting ||
                                                !dispatchedImage ||
                                                !courierCompanyId ||
                                                !trackingNumber.trim()
                                            }
                                            onClick={handleDispatchProducts}
                                        >
                                            {submitting ? (
                                                <>
                                                    <Spinner size="sm" className="me-2" />
                                                    Dispatching...
                                                </>
                                            ) : (
                                                "✓ Dispatch Products"
                                            )}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Col>
                )}
            </Row>

            {/* Camera Modal */}
            <Modal show={cameraOpen} onHide={stopCamera} centered size="lg" backdrop="static">
                <Modal.Header closeButton closeVariant="white" style={{ background: "#000", color: "#fff" }}>
                    <Modal.Title>Take Dispatch Photo</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ background: "#000", padding: 0 }}>
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        style={{ width: "100%", maxHeight: "70vh", objectFit: "contain", display: "block" }}
                    />
                </Modal.Body>
                <Modal.Footer style={{ background: "#000" }}>
                    <Button variant="secondary" onClick={stopCamera}>Cancel</Button>
                    <Button variant="primary" onClick={capturePhoto}>📸 Capture Photo</Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}